// src/components/jnanadeepam/DailyPrelimsQuiz.jsx
import React, { useState, useEffect, useRef } from "react";
import {
  Clock,
  CheckCircle,
  XCircle,
  HelpCircle,
  AlertCircle,
  RotateCcw,
  Award,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  Bookmark,
  Share2,
  Lock,
  Calendar,
  Folder,
  User,
  Phone,
  Mail,
  BookOpen,
} from "lucide-react";

export default function DailyPrelimsQuiz({ quizzes }) {
  const [selectedQuizIndex, setSelectedQuizIndex] = useState(0);
  const quiz = quizzes[selectedQuizIndex] || quizzes[0];

  // User details state (pre-filled with default details as shown in user screenshot, remembered in localStorage)
  const [candidate, setCandidate] = useState(() => {
    try {
      const saved = localStorage.getItem("selvas_quiz_candidate");
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.name && parsed.phone) return parsed;
      }
      return {
        name: "Lavanya Rengaraj",
        phone: "9345283928",
        email: "lavanyarengaraj739@gmail.com",
      };
    } catch (e) {
      return {
        name: "Lavanya Rengaraj",
        phone: "9345283928",
        email: "lavanyarengaraj739@gmail.com",
      };
    }
  });

  const [formErrors, setFormErrors] = useState({});

  // Quiz progression state: 'DIRECTORY' | 'ENTRY' | 'ACTIVE' | 'ANALYSIS'
  const [quizState, setQuizState] = useState("DIRECTORY");

  // Filter & Search states
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedYear, setSelectedYear] = useState("2026");
  const [selectedMonth, setSelectedMonth] = useState("October");
  const [selectedFolder, setSelectedFolder] = useState("All");
  const [showOlderYears, setShowOlderYears] = useState(false);

  const prelimsFolders = [
    "All",
    "Polity",
    "History",
    "Geography",
    "Economy",
    "Environment",
    "Science & Technology",
    "Current Affairs",
    "Art & Culture",
    "CSAT",
  ];

  const monthScrollRef = useRef(null);

  // Month list with quiz counts
  const monthsList = [
    { name: "October", quizzesCount: 27 },
    { name: "September", quizzesCount: 27 },
    { name: "August", quizzesCount: 27 },
    { name: "July", quizzesCount: 29 },
    { name: "June", quizzesCount: 28 },
    { name: "May", quizzesCount: 26 },
    { name: "April", quizzesCount: 27 },
    { name: "March", quizzesCount: 27 },
    { name: "February", quizzesCount: 25 },
    { name: "January", quizzesCount: 28 },
  ];

  // In-quiz states
  const [currentQIndex, setCurrentQIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState({}); // { [qId]: optionIndex }
  const [markedForReview, setMarkedForReview] = useState({}); // { [qId]: boolean }
  const [timeLeft, setTimeLeft] = useState(quiz.timeMinutes * 60);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [timeSpentSeconds, setTimeSpentSeconds] = useState(0);
  const [analysisFilter, setAnalysisFilter] = useState("ALL"); // 'ALL' | 'CORRECT' | 'WRONG' | 'SKIPPED'

  const timerRef = useRef(null);

  // Scroll Month Carousel
  const scrollMonths = (direction) => {
    if (monthScrollRef.current) {
      const offset = direction === "left" ? -260 : 260;
      monthScrollRef.current.scrollBy({ left: offset, behavior: "smooth" });
    }
  };

  // Timer effect
  useEffect(() => {
    if (quizState === "ACTIVE") {
      timerRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            handleSubmitQuiz();
            return 0;
          }
          return prev - 1;
        });
        setTimeSpentSeconds((prev) => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [quizState]);

  // When selected quiz changes, update timer
  useEffect(() => {
    setTimeLeft(quiz.timeMinutes * 60);
    setCurrentQIndex(0);
    setUserAnswers({});
    setMarkedForReview({});
    setTimeSpentSeconds(0);
  }, [selectedQuizIndex, quiz]);

  // Filter quizzes according to Year, Month, and Search Query
  const displayedQuizzes = quizzes.filter((item) => {
    // Year filter
    const matchesYear = selectedYear
      ? item.year === selectedYear || (item.date && item.date.startsWith(selectedYear))
      : true;

    // Month filter
    const matchesMonth = selectedMonth
      ? item.monthFull?.toLowerCase() === selectedMonth.toLowerCase()
      : true;

    // Search query filter (matches title, display date, or topic in questions)
    const qText = searchQuery.trim().toLowerCase();
    const matchesSearch =
      qText === "" ||
      item.title.toLowerCase().includes(qText) ||
      (item.displayDate && item.displayDate.toLowerCase().includes(qText)) ||
      (item.questions &&
        item.questions.some((q) => q.topic && q.topic.toLowerCase().includes(qText)));

    // Folder filter
    const matchesFolder =
      selectedFolder === "All" ||
      (item.folder && item.folder.toLowerCase().includes(selectedFolder.toLowerCase())) ||
      (item.subject && item.subject.toLowerCase().includes(selectedFolder.toLowerCase())) ||
      (item.title && item.title.toLowerCase().includes(selectedFolder.toLowerCase()));

    return matchesYear && matchesMonth && matchesSearch && matchesFolder;
  });

  // Select quiz from directory
  const handleChooseQuiz = (quizItem) => {
    const originalIndex = quizzes.findIndex((q) => q.id === quizItem.id);
    setSelectedQuizIndex(originalIndex !== -1 ? originalIndex : 0);
    setQuizState("ENTRY");
    window.scrollTo({ top: 320, behavior: "smooth" });
  };

  // Validate form and start quiz
  const handleStartQuiz = (e) => {
    e.preventDefault();
    const errors = {};
    if (!candidate.name.trim()) {
      errors.name = "Your name is required to begin the quiz.";
    }
    const cleanPhone = candidate.phone.replace(/\D/g, "");
    if (!candidate.phone.trim()) {
      errors.phone = "Phone number is required.";
    } else if (cleanPhone.length < 10) {
      errors.phone = "Please enter a valid 10-digit mobile number.";
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    // Save details to localStorage
    try {
      localStorage.setItem("selvas_quiz_candidate", JSON.stringify(candidate));
    } catch (err) {
      console.warn("Could not save to localStorage", err);
    }

    setQuizState("ACTIVE");
    setTimeLeft(quiz.timeMinutes * 60);
    setCurrentQIndex(0);
    setUserAnswers({});
    setMarkedForReview({});
    setTimeSpentSeconds(0);
    window.scrollTo({ top: 320, behavior: "smooth" });
  };

  // Select option
  const handleSelectOption = (qId, optionIndex) => {
    setUserAnswers((prev) => ({
      ...prev,
      [qId]: optionIndex,
    }));
  };

  // Clear option
  const handleClearResponse = (qId) => {
    setUserAnswers((prev) => {
      const copy = { ...prev };
      delete copy[qId];
      return copy;
    });
  };

  // Toggle mark for review
  const handleToggleReview = (qId) => {
    setMarkedForReview((prev) => ({
      ...prev,
      [qId]: !prev[qId],
    }));
  };

  // Submit test
  const handleSubmitQuiz = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setShowConfirmModal(false);
    setQuizState("ANALYSIS");
    window.scrollTo({ top: 320, behavior: "smooth" });
  };

  // Restart quiz
  const handleRetakeQuiz = () => {
    setUserAnswers({});
    setMarkedForReview({});
    setTimeLeft(quiz.timeMinutes * 60);
    setTimeSpentSeconds(0);
    setCurrentQIndex(0);
    setQuizState("ENTRY");
    window.scrollTo({ top: 320, behavior: "smooth" });
  };

  // Format time
  const formatTimer = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  // Calculate scores
  const totalQuestions = quiz.questions.length;
  let correctCount = 0;
  let wrongCount = 0;
  let skippedCount = 0;

  quiz.questions.forEach((q) => {
    const answer = userAnswers[q.qId];
    if (answer === undefined) {
      skippedCount++;
    } else if (answer === q.correctAnswer) {
      correctCount++;
    } else {
      wrongCount++;
    }
  });

  const attemptedCount = correctCount + wrongCount;
  const rawScore =
    correctCount * quiz.marksPerCorrect - wrongCount * quiz.negativeMarks;
  const totalMarks = totalQuestions * quiz.marksPerCorrect;
  const finalScore = Math.max(0, rawScore).toFixed(2);
  const accuracyPercentage =
    attemptedCount > 0 ? Math.round((correctCount / attemptedCount) * 100) : 0;

  // Sidebar Current Affairs List
  const sidebarCurrentAffairs = [
    { title: "UPSC CURRENT AFFAIRS – 30 OCTOBER 2026", date: "30 Oct 2026" },
    { title: "UPSC CURRENT AFFAIRS – 29 OCTOBER 2026", date: "29 Oct 2026" },
    { title: "UPSC CURRENT AFFAIRS – 28 OCTOBER 2026", date: "28 Oct 2026" },
    { title: "UPSC CURRENT AFFAIRS – 27 OCTOBER 2026", date: "27 Oct 2026" },
  ];

  return (
    <div className="w-full">
      {/* ========================================================================= */}
      {/* SCREEN 1: DIRECTORY VIEW (Search, Year, Month Carousel, Quiz Cards)        */}
      {/* ========================================================================= */}
      {quizState === "DIRECTORY" && (
        <div className="space-y-6">
          {/* Top Filter Card: Search Quiz by date/title & Choose Year */}
          <div className="bg-white rounded-3xl p-6 md:p-8 shadow-xs border border-slate-200/90">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
              {/* Left: Search input */}
              <div className="flex-1">
                <label className="block text-sm font-extrabold text-slate-900 mb-2">
                  Search quiz by date/title
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search Current Affairs Quiz..."
                    className="w-full px-5 py-3 bg-white border border-slate-200 rounded-2xl text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all shadow-xs"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery("")}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-semibold text-slate-400 hover:text-slate-700 bg-slate-100 hover:bg-slate-200 px-2 py-1 rounded-md cursor-pointer"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>

              {/* Right: Choose Year */}
              <div className="md:w-auto">
                <label className="block text-sm font-extrabold text-slate-900 mb-2">
                  Choose year
                </label>
                <div className="flex flex-wrap items-center gap-2">
                  {["2026", "2025", "2024"].map((yr) => (
                    <button
                      key={yr}
                      type="button"
                      onClick={() => setSelectedYear(yr)}
                      className={`px-5 py-2.5 rounded-full text-xs font-extrabold transition-all cursor-pointer ${
                        selectedYear === yr
                          ? "bg-[#0f172a] text-white shadow-xs"
                          : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
                      }`}
                    >
                      {yr}
                    </button>
                  ))}

                  {/* Older Years Dropdown */}
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowOlderYears(!showOlderYears)}
                      className="px-4 py-2.5 rounded-full text-xs font-extrabold bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 flex items-center gap-1.5 transition-all cursor-pointer"
                    >
                      <span>Older years</span>
                      <ChevronDown
                        size={14}
                        className={`transition-transform ${
                          showOlderYears ? "rotate-180" : ""
                        }`}
                      />
                    </button>

                    {showOlderYears && (
                      <div className="absolute right-0 top-full mt-2 bg-white border border-slate-200 rounded-2xl shadow-xl p-2 z-30 min-w-[140px] animate-scaleUp">
                        {["2023", "2022", "2021", "2020"].map((yr) => (
                          <button
                            key={yr}
                            type="button"
                            onClick={() => {
                              setSelectedYear(yr);
                              setShowOlderYears(false);
                            }}
                            className="w-full text-left px-3.5 py-2 text-xs font-bold rounded-xl hover:bg-red-50 hover:text-red-700 text-slate-700 transition cursor-pointer"
                          >
                            {yr}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Month Wise Carousel Slider */}
          <div className="relative flex items-center">
            {/* Left Scroll Button */}
            <button
              type="button"
              onClick={() => scrollMonths("left")}
              className="w-8 h-8 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-50 shadow-xs z-10 flex-shrink-0 mr-2 cursor-pointer transition"
            >
              <ChevronLeft size={16} />
            </button>

            {/* Scrollable Month List */}
            <div
              ref={monthScrollRef}
              className="flex items-center gap-3 overflow-x-auto py-2 px-1 flex-1 no-scrollbar"
              style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
            >
              {monthsList.map((m) => {
                const isSelected = selectedMonth === m.name;
                return (
                  <button
                    key={m.name}
                    type="button"
                    onClick={() => setSelectedMonth(m.name)}
                    className={`min-w-[125px] md:min-w-[135px] p-3.5 md:p-4 rounded-2xl border text-center transition-all cursor-pointer flex flex-col justify-center items-center flex-shrink-0 ${
                      isSelected
                        ? "bg-red-50/50 border-red-300 shadow-xs"
                        : "bg-white border-slate-200/90 hover:border-slate-300 hover:bg-slate-50/50"
                    }`}
                  >
                    <span
                      className={`text-sm md:text-base font-bold mb-0.5 ${
                        isSelected ? "text-red-600" : "text-slate-800"
                      }`}
                    >
                      {m.name}
                    </span>
                    <span className="text-[11px] text-slate-400 font-medium">
                      {m.quizzesCount} quizzes
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Right Scroll Button */}
            <button
              type="button"
              onClick={() => scrollMonths("right")}
              className="w-8 h-8 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-50 shadow-xs z-10 flex-shrink-0 ml-2 cursor-pointer transition"
            >
              <ChevronRight size={16} />
            </button>
          </div>

          {/* Heading */}
          <div>
            <span className="text-xs uppercase font-extrabold tracking-wider text-red-600 block mb-1">
              DAILY QUIZZES
            </span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight mb-1.5">
              Choose a quiz and start practicing
            </h2>
            <p className="text-slate-500 text-sm md:text-base">
              Daily current affairs practice for UPSC Prelims preparation organized by subject folders.
            </p>
          </div>

          {/* Subject Folders Filter Bar */}
          <div className="bg-white rounded-2xl p-4 md:p-5 border border-slate-200/90 shadow-2xs">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Folder size={16} className="text-amber-600" />
                <span className="text-xs font-bold text-slate-900 uppercase tracking-wider">
                  Browse by Subject Folder
                </span>
                <span className="text-[11px] font-medium text-slate-400">
                  ({prelimsFolders.length - 1} folders)
                </span>
              </div>
              {selectedFolder !== "All" && (
                <button
                  type="button"
                  onClick={() => setSelectedFolder("All")}
                  className="text-xs font-bold text-red-600 hover:text-red-700 hover:underline cursor-pointer"
                >
                  Show All Folders
                </button>
              )}
            </div>
            <div className="flex items-center gap-2 overflow-x-auto pb-1 no-scrollbar">
              {prelimsFolders.map((f) => {
                const isSelected = selectedFolder === f;
                return (
                  <button
                    key={f}
                    type="button"
                    onClick={() => setSelectedFolder(f)}
                    className={`px-3.5 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all cursor-pointer flex items-center gap-1.5 ${
                      isSelected
                        ? "bg-amber-600 text-white shadow-xs scale-102"
                        : "bg-slate-50 hover:bg-slate-100 text-slate-700 border border-slate-200/80"
                    }`}
                  >
                    <Folder size={12} className={isSelected ? "text-amber-200" : "text-amber-600"} />
                    <span>{f}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Subheading row with dynamic month, year and count badge */}
          <div className="flex items-center justify-between border-b border-slate-200/80 pb-3 pt-1">
            <div className="flex items-center gap-2">
              <h3 className="text-lg md:text-xl font-bold text-slate-900">
                {selectedFolder !== "All" ? `${selectedFolder} Quizzes` : `${selectedMonth} Quizzes`} {selectedYear}
              </h3>
              {selectedFolder !== "All" && (
                <span className="text-[11px] font-bold px-2 py-0.5 rounded-md bg-amber-100 text-amber-900 border border-amber-200">
                  Folder: {selectedFolder}
                </span>
              )}
            </div>
            <span className="bg-blue-100 text-blue-700 font-bold text-xs px-3 py-1 rounded-full border border-blue-200">
              {displayedQuizzes.length} quizzes
            </span>
          </div>

          {/* Main 2-Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            {/* Left 8 Columns: Grid of Quiz Cards */}
            <div className="lg:col-span-8">
              {displayedQuizzes.length === 0 ? (
                <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center space-y-3">
                  <p className="text-slate-600 text-sm">
                    No quizzes found matching your search in{" "}
                    <strong>{selectedMonth} {selectedYear}</strong>.
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setSearchQuery("");
                      setSelectedMonth("October");
                      setSelectedYear("2026");
                    }}
                    className="text-xs font-bold text-red-700 hover:underline"
                  >
                    Reset filters to October 2026
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {displayedQuizzes.map((item) => (
                    <div
                      key={item.id}
                      className="bg-white rounded-2xl border border-slate-200/90 shadow-sm hover:shadow-md transition-all p-6 relative flex flex-col justify-between overflow-hidden group"
                    >
                      {/* Top red accent line */}
                      <div className="absolute top-0 left-0 right-0 h-1 bg-red-600" />

                      <div>
                        {/* Date + Latest Badge */}
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-baseline gap-2">
                            <span className="text-2xl font-black text-red-600 leading-none">
                              {item.day || "30"}
                            </span>
                            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                              {item.month || "OCT"}
                            </span>
                          </div>

                          {item.isLatest && (
                            <span className="text-[10px] font-black uppercase tracking-wider px-2.5 py-0.5 rounded-full border border-red-500 text-red-600 bg-red-50">
                              LATEST
                            </span>
                          )}
                        </div>

                        {/* Folder Badge & Test Date */}
                        <div className="flex items-center gap-1.5 flex-wrap mb-2">
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-amber-50 text-amber-900 border border-amber-200">
                            <Folder size={11} className="text-amber-700" />
                            {item.folder || item.subject || "Polity"}
                          </span>
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-600 border border-slate-200">
                            <Calendar size={11} />
                            {item.date || item.displayDate || `${item.day || "30"} ${item.month || "OCT"} 2026`}
                          </span>
                        </div>

                        {/* Quiz Title */}
                        <h4
                          onClick={() => handleChooseQuiz(item)}
                          className="text-sm md:text-base font-bold text-slate-900 leading-snug my-2 hover:text-red-600 transition-colors cursor-pointer line-clamp-2"
                        >
                          {item.title}
                        </h4>
                      </div>

                      {/* Red Take Quiz Button */}
                      <div className="pt-3">
                        <button
                          type="button"
                          onClick={() => handleChooseQuiz(item)}
                          className="w-full bg-[#e53e3e] hover:bg-[#c53030] active:bg-[#9b2c2c] text-white font-bold py-2.5 px-4 rounded-xl text-sm transition-all shadow-sm hover:shadow flex items-center justify-center cursor-pointer"
                        >
                          Take Quiz
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Right 4 Columns: Sidebars */}
            <div className="lg:col-span-4 space-y-6">
              {/* Card 1: Prelims Test Series 2027 Promotion */}
              <div className="bg-gradient-to-b from-[#111827] via-[#1a1024] to-[#3b0d18] text-white rounded-3xl p-7 shadow-xl space-y-4">
                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-200 bg-white/10 px-3 py-1 rounded-full inline-block">
                  PRELIMS TEST SERIES 2027
                </span>
                <h3 className="text-2xl font-extrabold text-white leading-tight">
                  UPSC Prelims Test Series 2027
                </h3>
                <p className="text-slate-300 text-xs md:text-sm leading-relaxed">
                  Prepare with structured GS, CSAT and full-length Prelims tests
                  designed for serious UPSC aspirants.
                </p>
                <div className="pt-1">
                  <a
                    href="/testseries#prelims-series"
                    className="inline-block bg-white hover:bg-slate-100 text-slate-950 font-bold px-6 py-2.5 rounded-full text-xs shadow-md transition-all text-center"
                  >
                    Explore Test Series
                  </a>
                </div>
              </div>

              {/* Card 2: Latest Current Affairs */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-4">
                <h3 className="font-bold text-slate-900 text-base">
                  Latest Current Affairs
                </h3>

                <div className="space-y-3">
                  {sidebarCurrentAffairs.map((ca, i) => (
                    <a
                      key={i}
                      href="/current#daily"
                      className="flex items-center justify-between p-3.5 rounded-xl border border-slate-100 hover:border-slate-300 hover:bg-slate-50 transition-all group"
                    >
                      <div className="space-y-0.5">
                        <h5 className="text-xs font-bold text-slate-800 group-hover:text-red-700 transition-colors uppercase">
                          {ca.title}
                        </h5>
                        <span className="text-[11px] text-slate-400">
                          {ca.date}
                        </span>
                      </div>
                      <span className="w-6 h-6 rounded-full border border-slate-200 flex items-center justify-center text-red-500 group-hover:bg-red-50 group-hover:border-red-400 transition-all flex-shrink-0 ml-2">
                        <ChevronRight size={14} />
                      </span>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* SCREEN 2: QUIZ ENTRY & DETAILS REGISTRATION (Matches Image 2)             */}
      {/* ========================================================================= */}
      {quizState === "ENTRY" && (
        <div className="space-y-6">
          {/* Breadcrumb / Back button */}
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => setQuizState("DIRECTORY")}
              className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-600 hover:text-red-700 transition bg-white px-3.5 py-2 rounded-xl border border-slate-200 shadow-sm cursor-pointer"
            >
              <ChevronLeft size={16} /> Back to Quizzes
            </button>

            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider hidden sm:inline">
              Selected: <strong className="text-slate-800">{quiz.displayDate}</strong>
            </span>
          </div>

          {/* 2-Column Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            {/* Left 8 Cols: Quiz Entry Description & Details Form */}
            <div className="lg:col-span-8 bg-white rounded-2xl shadow-sm border border-slate-200 p-6 md:p-8 space-y-6">
              {/* Introduction paragraph */}
              <p className="text-slate-600 text-xs md:text-sm leading-relaxed">
                The Current Affairs Quiz is a daily quiz based on the DAILY
                CURRENT AFFAIRS AND PIB SUMMARY from the previous day, as posted
                on our website. It covers all relevant news sources and is
                designed to test your knowledge of current events. Solving these
                questions will help you retain both concepts and facts relevant
                to the UPSC IAS civil services exam.
              </p>

              {/* Blue Header Info Box */}
              <div className="bg-[#eff6ff] border border-[#bfdbfe] rounded-2xl p-5 md:p-6 space-y-1">
                <h4 className="font-bold text-slate-900 text-sm md:text-base flex items-center gap-2">
                  <User size={18} className="text-[#2563eb]" /> Enter your details
                  to begin
                </h4>
                <p className="text-xs md:text-sm text-slate-600">
                  Your name and phone number are required. Email is optional;
                  this device will remember your attempt securely.
                </p>
              </div>

              {/* Details Form */}
              <form onSubmit={handleStartQuiz} className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* YOUR NAME * */}
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                      YOUR NAME <span className="text-red-600">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                        <User size={16} />
                      </div>
                      <input
                        type="text"
                        required
                        value={candidate.name}
                        onChange={(e) => {
                          setCandidate({ ...candidate, name: e.target.value });
                          if (formErrors.name)
                            setFormErrors({ ...formErrors, name: "" });
                        }}
                        placeholder="Enter your name"
                        className={`w-full pl-10 pr-4 py-2.5 bg-white text-sm border rounded-xl focus:outline-none focus:ring-2 transition ${
                          formErrors.name
                            ? "border-red-400 focus:ring-red-300"
                            : "border-slate-300 focus:ring-red-500 focus:border-red-500"
                        }`}
                      />
                    </div>
                    {formErrors.name && (
                      <p className="text-xs text-red-600 mt-1 font-medium">
                        {formErrors.name}
                      </p>
                    )}
                  </div>

                  {/* PHONE NUMBER * */}
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                      PHONE NUMBER <span className="text-red-600">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                        <Phone size={16} />
                      </div>
                      <input
                        type="tel"
                        required
                        value={candidate.phone}
                        onChange={(e) => {
                          setCandidate({
                            ...candidate,
                            phone: e.target.value,
                          });
                          if (formErrors.phone)
                            setFormErrors({ ...formErrors, phone: "" });
                        }}
                        placeholder="Enter 10-digit mobile number"
                        className={`w-full pl-10 pr-4 py-2.5 bg-white text-sm border rounded-xl focus:outline-none focus:ring-2 transition ${
                          formErrors.phone
                            ? "border-red-400 focus:ring-red-300"
                            : "border-slate-300 focus:ring-red-500 focus:border-red-500"
                        }`}
                      />
                    </div>
                    {formErrors.phone && (
                      <p className="text-xs text-red-600 mt-1 font-medium">
                        {formErrors.phone}
                      </p>
                    )}
                  </div>
                </div>

                {/* EMAIL ADDRESS (OPTIONAL) */}
                <div>
                  <div className="flex justify-between items-center mb-1.5">
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-700">
                      EMAIL ADDRESS
                    </label>
                    <span className="text-[10px] font-bold text-slate-600 uppercase tracking-wider">
                      OPTIONAL
                    </span>
                  </div>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                      <Mail size={16} />
                    </div>
                    <input
                      type="email"
                      value={candidate.email}
                      onChange={(e) =>
                        setCandidate({ ...candidate, email: e.target.value })
                      }
                      placeholder="your@email.com (optional)"
                      className="w-full pl-10 pr-4 py-2.5 bg-white text-sm border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition"
                    />
                  </div>
                </div>

                {/* Submit Row */}
                <div className="pt-2 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <button
                    type="submit"
                    className="bg-[#b91c1c] hover:bg-[#991b1b] text-white font-bold px-7 py-3 rounded-xl text-sm transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2 group cursor-pointer"
                  >
                    Start Quiz{" "}
                    <ChevronRight
                      size={18}
                      className="transition-transform group-hover:translate-x-1"
                    />
                  </button>

                  <div className="text-xs text-slate-500 flex items-center gap-1.5">
                    <Lock size={14} className="text-amber-600 flex-shrink-0" />
                    <span>
                      Details are remembered on this device. A different quiz
                      will ask you to confirm them once.
                    </span>
                  </div>
                </div>
              </form>
            </div>

            {/* Right 4 Cols: Recent Quizzes & Telegram Card */}
            <div className="lg:col-span-4 space-y-6">
              {/* Numbered Quizzes List */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-3">
                <h4 className="font-bold text-slate-900 text-sm border-b border-slate-100 pb-3">
                  Recent Daily Prelims Quizzes
                </h4>

                <div className="space-y-2.5">
                  {quizzes.slice(0, 5).map((item, idx) => {
                    const isCurrent = idx === selectedQuizIndex;
                    return (
                      <div
                        key={item.id}
                        onClick={() => setSelectedQuizIndex(idx)}
                        className={`p-3.5 rounded-xl border cursor-pointer transition-all ${
                          isCurrent
                            ? "bg-red-50/70 border-red-300 shadow-xs"
                            : "bg-slate-50/60 border-slate-200 hover:bg-slate-100"
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <span
                            className={`w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5 ${
                              isCurrent
                                ? "bg-red-700 text-white"
                                : "bg-slate-200 text-slate-700"
                            }`}
                          >
                            {idx + 1}
                          </span>
                          <div className="space-y-1">
                            <h5
                              className={`text-xs font-bold leading-snug line-clamp-2 ${
                                isCurrent
                                  ? "text-red-900"
                                  : "text-slate-800 hover:text-red-700"
                              }`}
                            >
                              {item.title}
                            </h5>
                            <div className="text-[11px] text-slate-400 flex items-center gap-1">
                              <Calendar size={11} /> Posted on {item.displayDate}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Telegram Card */}
              <div className="bg-gradient-to-br from-blue-900 to-indigo-950 text-white rounded-2xl p-6 shadow-md space-y-3">
                <span className="text-[10px] font-bold uppercase tracking-widest text-amber-300 bg-white/10 px-2.5 py-0.5 rounded-full inline-block">
                  SELVA'S MENTORSHIP
                </span>
                <h4 className="text-lg font-bold">
                  Join Our Official Telegram
                </h4>
                <p className="text-xs text-white/80 leading-relaxed">
                  Get daily current affairs PDFs, Prelims answer keys & Mains
                  evaluation reviews straight to your phone.
                </p>
                <a
                  href="https://t.me/Selvasanthro"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-white text-blue-950 font-bold px-4 py-2 rounded-xl text-xs hover:bg-amber-300 transition-colors shadow"
                >
                  <Share2 size={14} /> Join Telegram Channel
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* SCREEN 3: ACTIVE TEST TAKING (16:00 Timer, Questions, Navigator)          */}
      {/* ========================================================================= */}
      {quizState === "ACTIVE" && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Main Question Area (8 cols) */}
          <div className="lg:col-span-8 bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            {/* Status Bar */}
            <div className="bg-slate-900 text-white p-4 md:px-6 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="text-xs font-bold uppercase bg-red-600 text-white px-2.5 py-1 rounded">
                  Question {currentQIndex + 1} of {totalQuestions}
                </span>
                <span className="text-xs text-slate-300 hidden sm:inline">
                  Candidate: <strong className="text-white">{candidate.name}</strong>
                </span>
              </div>

              {/* Countdown Timer */}
              <div
                className={`flex items-center gap-2 font-mono font-bold text-sm px-3.5 py-1.5 rounded-lg border transition ${
                  timeLeft < 120
                    ? "bg-red-600 text-white border-red-500 animate-pulse"
                    : timeLeft < 300
                    ? "bg-amber-500 text-white border-amber-400"
                    : "bg-slate-800 text-emerald-400 border-slate-700"
                }`}
              >
                <Clock size={16} />
                <span>Time Left: {formatTimer(timeLeft)}</span>
              </div>
            </div>

            {/* Question Details */}
            {(() => {
              const currentQ = quiz.questions[currentQIndex];
              const selectedOption = userAnswers[currentQ.qId];
              const isMarked = !!markedForReview[currentQ.qId];

              return (
                <div className="p-6 md:p-8 space-y-6">
                  {/* Topic badge and Mark for Review */}
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-3">
                    <span className="text-xs font-semibold text-slate-500 bg-slate-100 px-3 py-1 rounded-full">
                      Topic: <strong className="text-slate-800">{currentQ.topic}</strong>
                    </span>

                    <button
                      type="button"
                      onClick={() => handleToggleReview(currentQ.qId)}
                      className={`text-xs font-bold px-3 py-1.5 rounded-lg border transition flex items-center gap-1.5 cursor-pointer ${
                        isMarked
                          ? "bg-purple-100 text-purple-800 border-purple-300"
                          : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
                      }`}
                    >
                      <Bookmark size={14} className={isMarked ? "fill-purple-700" : ""} />
                      {isMarked ? "Marked for Review" : "Mark for Review"}
                    </button>
                  </div>

                  {/* Question Text */}
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <span className="w-7 h-7 rounded-full bg-red-700 text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                        {currentQIndex + 1}
                      </span>
                      <h4 className="text-base md:text-lg font-semibold text-slate-900 leading-relaxed whitespace-pre-line">
                        {currentQ.question}
                      </h4>
                    </div>
                  </div>

                  {/* 4 Radio Options */}
                  <div className="space-y-3 pt-2 pl-2 md:pl-10">
                    {currentQ.options.map((opt, optIdx) => {
                      const isSelected = selectedOption === optIdx;
                      return (
                        <label
                          key={optIdx}
                          onClick={() => handleSelectOption(currentQ.qId, optIdx)}
                          className={`flex items-start gap-3 p-4 rounded-xl border text-sm font-medium cursor-pointer transition-all ${
                            isSelected
                              ? "bg-red-50 border-red-500 text-red-950 shadow-sm"
                              : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
                          }`}
                        >
                          <input
                            type="radio"
                            name={`question-${currentQ.qId}`}
                            checked={isSelected}
                            onChange={() => handleSelectOption(currentQ.qId, optIdx)}
                            className="mt-1 text-red-700 focus:ring-red-600"
                          />
                          <span className="flex-1 leading-relaxed">{opt}</span>
                        </label>
                      );
                    })}
                  </div>

                  {/* Question Navigation Footer */}
                  <div className="border-t border-slate-200 pt-5 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex gap-2">
                      <button
                        type="button"
                        disabled={currentQIndex === 0}
                        onClick={() => setCurrentQIndex((prev) => Math.max(0, prev - 1))}
                        className="px-4 py-2 border border-slate-300 rounded-xl text-xs font-bold text-slate-700 hover:bg-slate-100 disabled:opacity-40 transition flex items-center gap-1 cursor-pointer"
                      >
                        <ChevronLeft size={16} /> Previous
                      </button>

                      <button
                        type="button"
                        disabled={currentQIndex === totalQuestions - 1}
                        onClick={() =>
                          setCurrentQIndex((prev) => Math.min(totalQuestions - 1, prev + 1))
                        }
                        className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold disabled:opacity-40 transition flex items-center gap-1 cursor-pointer"
                      >
                        Next <ChevronRight size={16} />
                      </button>
                    </div>

                    <div className="flex items-center gap-3">
                      {selectedOption !== undefined && (
                        <button
                          type="button"
                          onClick={() => handleClearResponse(currentQ.qId)}
                          className="text-xs font-semibold text-slate-500 hover:text-red-700 underline cursor-pointer"
                        >
                          Clear Response
                        </button>
                      )}

                      <button
                        type="button"
                        onClick={() => setShowConfirmModal(true)}
                        className="px-6 py-2.5 bg-red-700 hover:bg-red-800 text-white rounded-xl text-xs font-bold transition shadow cursor-pointer"
                      >
                        Submit Quiz
                      </button>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Right Question Palette (4 cols) */}
          <div className="lg:col-span-4 bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-5">
            <h4 className="font-bold text-slate-900 text-sm border-b border-slate-100 pb-3 flex items-center justify-between">
              <span>Question Palette</span>
              <span className="text-xs text-slate-400 font-normal">
                {attemptedCount} / {totalQuestions} Answered
              </span>
            </h4>

            {/* Palette Grid */}
            <div className="grid grid-cols-5 gap-2.5">
              {quiz.questions.map((q, idx) => {
                const isCurrent = idx === currentQIndex;
                const isAnswered = userAnswers[q.qId] !== undefined;
                const isMarked = !!markedForReview[q.qId];

                let buttonClass =
                  "border border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100";
                if (isCurrent) {
                  buttonClass = "ring-2 ring-red-600 bg-white font-black text-red-700 shadow";
                } else if (isMarked) {
                  buttonClass = "bg-purple-100 border-purple-300 text-purple-800 font-bold";
                } else if (isAnswered) {
                  buttonClass = "bg-emerald-600 border-emerald-600 text-white font-bold";
                }

                return (
                  <button
                    key={q.qId}
                    type="button"
                    onClick={() => setCurrentQIndex(idx)}
                    className={`h-10 rounded-xl text-xs flex items-center justify-center transition-all cursor-pointer ${buttonClass}`}
                  >
                    {idx + 1}
                  </button>
                );
              })}
            </div>

            {/* Legend */}
            <div className="border-t border-slate-100 pt-3 space-y-2 text-[11px] text-slate-600">
              <div className="flex items-center gap-2">
                <span className="w-3.5 h-3.5 rounded bg-emerald-600 inline-block" />
                <span>Answered</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3.5 h-3.5 rounded bg-purple-100 border border-purple-300 inline-block" />
                <span>Marked for Review</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3.5 h-3.5 rounded bg-slate-50 border border-slate-200 inline-block" />
                <span>Not Visited / Skipped</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* SCREEN 4: DETAILED SCORE & CONCEPT ANALYSIS                               */}
      {/* ========================================================================= */}
      {quizState === "ANALYSIS" && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden space-y-8 p-6 md:p-8">
          {/* Header */}
          <div className="border-b border-slate-200 pb-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-emerald-700 bg-emerald-100 px-3 py-1 rounded-full">
                Completed • Performance Analysis
              </span>
              <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900 mt-2">
                {quiz.shortTitle}
              </h2>
              <p className="text-xs md:text-sm text-slate-500 mt-1">
                Candidate: <strong className="text-slate-800">{candidate.name}</strong> • Phone:{" "}
                <strong className="text-slate-800">{candidate.phone}</strong> • Time Taken:{" "}
                <strong className="text-slate-800">{formatTimer(timeSpentSeconds)}</strong>
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleRetakeQuiz}
                className="inline-flex items-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold px-4 py-2 rounded-xl text-xs transition cursor-pointer"
              >
                <RotateCcw size={14} /> Retake Quiz
              </button>
              <button
                type="button"
                onClick={() => setQuizState("DIRECTORY")}
                className="inline-flex items-center gap-2 bg-red-700 hover:bg-red-800 text-white font-bold px-4 py-2 rounded-xl text-xs transition shadow cursor-pointer"
              >
                <BookOpen size={14} /> Back to All Quizzes
              </button>
            </div>
          </div>

          {/* 4 Performance Metric Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 text-center space-y-1">
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                Total Score
              </span>
              <div className="text-2xl md:text-3xl font-extrabold text-red-700">
                {finalScore}
                <span className="text-xs text-slate-400 font-normal">
                  {" "}
                  / {totalMarks.toFixed(2)}
                </span>
              </div>
              <div className="text-[11px] text-slate-500">
                (+{quiz.marksPerCorrect} / -{quiz.negativeMarks})
              </div>
            </div>

            <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 text-center space-y-1">
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                Accuracy Rate
              </span>
              <div className="text-2xl md:text-3xl font-extrabold text-blue-800">
                {accuracyPercentage}%
              </div>
              <div className="text-[11px] text-slate-500">
                {correctCount} correct of {attemptedCount} attempted
              </div>
            </div>

            <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 text-center space-y-1">
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                Attempt Breakdown
              </span>
              <div className="flex items-center justify-center gap-2 text-sm font-bold pt-1">
                <span className="text-emerald-700">{correctCount} ✔</span>
                <span className="text-red-600">{wrongCount} ✖</span>
                <span className="text-slate-500">{skippedCount} ⚪</span>
              </div>
              <div className="text-[11px] text-slate-500">Correct / Wrong / Skipped</div>
            </div>

            <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 text-center space-y-1">
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                Time Utilized
              </span>
              <div className="text-2xl md:text-3xl font-extrabold text-slate-800">
                {formatTimer(timeSpentSeconds)}
              </div>
              <div className="text-[11px] text-slate-500">
                Allocated: {quiz.timeMinutes}:00 mins
              </div>
            </div>
          </div>

          {/* Mentor Feedback Banner */}
          <div
            className={`p-4 rounded-xl border flex items-start gap-3 ${
              Number(finalScore) >= 14
                ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                : Number(finalScore) >= 8
                ? "bg-blue-50 border-blue-200 text-blue-900"
                : "bg-amber-50 border-amber-200 text-amber-900"
            }`}
          >
            <Award size={22} className="flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-bold text-sm">
                {Number(finalScore) >= 14
                  ? "Outstanding Performance!"
                  : Number(finalScore) >= 8
                  ? "Good Attempt with Solid Fundamentals!"
                  : "Needs Focused Revision & Note-Taking"}
              </h4>
              <p className="text-xs mt-0.5 leading-relaxed">
                {Number(finalScore) >= 14
                  ? "Your accuracy in current events and static linkage is strong. Continue this daily rhythm to stay comfortably above the Prelims cut-off threshold."
                  : Number(finalScore) >= 8
                  ? "You have a solid base. Focus on the questions answered incorrectly to eliminate negative marking and push your score above 14."
                  : "Carefully review the explanations below. Re-read the Daily PIB and Newspaper editorial summaries before attempting the next quiz."}
              </p>
            </div>
          </div>

          {/* Filter Tabs for Solutions */}
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 pb-3">
            <h3 className="font-bold text-slate-900 text-lg">
              Question-by-Question Solution & Analysis
            </h3>

            <div className="flex items-center gap-1.5 text-xs font-semibold">
              {[
                { id: "ALL", label: `All (${totalQuestions})` },
                { id: "CORRECT", label: `Correct (${correctCount})` },
                { id: "WRONG", label: `Incorrect (${wrongCount})` },
                { id: "SKIPPED", label: `Skipped (${skippedCount})` },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setAnalysisFilter(tab.id)}
                  className={`px-3 py-1.5 rounded-lg transition cursor-pointer ${
                    analysisFilter === tab.id
                      ? "bg-slate-900 text-white"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Solutions List */}
          <div className="space-y-6">
            {quiz.questions
              .filter((q) => {
                const answer = userAnswers[q.qId];
                if (analysisFilter === "CORRECT") return answer === q.correctAnswer;
                if (analysisFilter === "WRONG")
                  return answer !== undefined && answer !== q.correctAnswer;
                if (analysisFilter === "SKIPPED") return answer === undefined;
                return true;
              })
              .map((q) => {
                const answer = userAnswers[q.qId];
                const isCorrect = answer === q.correctAnswer;
                const isSkipped = answer === undefined;

                return (
                  <div
                    key={q.qId}
                    className={`p-6 rounded-2xl border space-y-4 ${
                      isCorrect
                        ? "bg-emerald-50/40 border-emerald-200"
                        : isSkipped
                        ? "bg-slate-50 border-slate-200"
                        : "bg-red-50/40 border-red-200"
                    }`}
                  >
                    {/* Header */}
                    <div className="flex items-center justify-between gap-2 border-b border-slate-200/60 pb-3">
                      <span className="text-xs font-bold text-slate-500 uppercase">
                        Question {q.qId} • {q.topic}
                      </span>
                      <span
                        className={`text-xs font-bold px-3 py-1 rounded-full ${
                          isCorrect
                            ? "bg-emerald-100 text-emerald-800"
                            : isSkipped
                            ? "bg-slate-200 text-slate-700"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {isCorrect ? "Correct (+2.00)" : isSkipped ? "Skipped (0.00)" : "Incorrect (-0.66)"}
                      </span>
                    </div>

                    {/* Question text */}
                    <h4 className="text-sm md:text-base font-semibold text-slate-900 whitespace-pre-line leading-relaxed">
                      {q.question}
                    </h4>

                    {/* Options status */}
                    <div className="space-y-2">
                      {q.options.map((opt, optIdx) => {
                        const isThisCorrect = optIdx === q.correctAnswer;
                        const isThisUserPick = optIdx === answer;

                        let optClass = "bg-white border-slate-200 text-slate-700";
                        if (isThisCorrect) {
                          optClass = "bg-emerald-100/70 border-emerald-400 text-emerald-950 font-bold";
                        } else if (isThisUserPick && !isThisCorrect) {
                          optClass = "bg-red-100/70 border-red-400 text-red-950 line-through";
                        }

                        return (
                          <div
                            key={optIdx}
                            className={`p-3 rounded-xl border text-xs md:text-sm flex items-center justify-between ${optClass}`}
                          >
                            <span>{opt}</span>
                            {isThisCorrect && (
                              <span className="text-emerald-700 font-bold text-xs flex items-center gap-1">
                                <CheckCircle size={14} /> Correct Answer
                              </span>
                            )}
                            {isThisUserPick && !isThisCorrect && (
                              <span className="text-red-700 font-bold text-xs flex items-center gap-1">
                                <XCircle size={14} /> Your Selection
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Detailed Concept & Explanation */}
                    <div className="p-4 bg-white rounded-xl border border-slate-200 text-sm space-y-1.5 shadow-xs">
                      <div className="font-bold text-slate-900 flex items-center gap-1.5 text-xs uppercase tracking-wide text-blue-900">
                        <HelpCircle size={15} /> Detailed Analysis & Concept:
                      </div>
                      <p className="text-slate-700 text-xs md:text-sm leading-relaxed">
                        {q.explanation}
                      </p>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* Confirmation Modal for Submitting */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-5 animate-scaleUp">
            <div className="flex items-center gap-3 text-amber-600">
              <AlertCircle size={28} />
              <h3 className="text-lg font-bold text-slate-900">
                Submit Your Quiz?
              </h3>
            </div>

            <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-2 text-xs text-slate-700">
              <div className="flex justify-between">
                <span>Total Questions:</span>
                <strong className="text-slate-900">{totalQuestions}</strong>
              </div>
              <div className="flex justify-between">
                <span>Answered:</span>
                <strong className="text-emerald-700">{attemptedCount}</strong>
              </div>
              <div className="flex justify-between">
                <span>Unattempted:</span>
                <strong className="text-slate-500">{skippedCount}</strong>
              </div>
              <div className="flex justify-between">
                <span>Time Remaining:</span>
                <strong className="text-blue-800">{formatTimer(timeLeft)}</strong>
              </div>
            </div>

            <p className="text-xs text-slate-600">
              Once submitted, you will receive your final score along with
              question-wise solutions and detailed concept analysis.
            </p>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowConfirmModal(false)}
                className="px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-100 rounded-xl transition cursor-pointer"
              >
                Go Back
              </button>
              <button
                type="button"
                onClick={handleSubmitQuiz}
                className="px-5 py-2 text-xs font-bold bg-red-700 hover:bg-red-800 text-white rounded-xl transition shadow cursor-pointer"
              >
                Yes, Submit Final Answers
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
